import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { HOURLY_STANDARD_DAY, monthlyToHourly, rate, totalWeekdaysIn } from "./data";

/**
 * The Pay/hour cell's editor — portaled to <body> (same reason as
 * PerfPopover: the table's own .lbr-scroll clips anything absolute inside
 * it) and anchored under the cell that opened it, so a 10%-wide column never
 * has to squeeze a mode switch AND a number input into its own width.
 *
 * Hourly is one field, unchanged from before. Monthly is the same field plus
 * a live preview of what it works out to per hour THIS period — the one
 * place the weekday math (data.js monthlyToHourly) becomes visible before
 * it's committed, so a typo reads as wrong before it's saved, not after.
 */
const PayPopover = ({
  anchor, mode, value, months, onModeChange, onValueChange, onCommit, onCancel,
}) => {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter") onCommit();
    };
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onCommit();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCommit, onCancel]);

  /* Placement: same rule PerfPopover uses — prefer opening below the cell,
     flip above when there isn't room, so it never has to get clamped into
     overlapping whatever sits just below (the table footer, most often,
     since the last roster row in a tall department opens right against it).
     Height is a static estimate rather than measured (PerfPopover does the
     same) — Monthly's extra hint line makes its box taller than Hourly's. */
  const style = useMemo(() => {
    if (!anchor) return null;
    const W = 224;
    const H = mode === "monthly" ? 150 : 104;
    const GAP = 6;
    const openAbove = anchor.top > H + GAP + 12 && window.innerHeight - anchor.bottom < H + GAP;
    const top = openAbove
      ? anchor.top - H - GAP
      : Math.min(anchor.bottom + GAP, window.innerHeight - H - 12);
    const left = Math.max(12, Math.min(anchor.left, window.innerWidth - W - 12));
    return { top, left, width: W };
  }, [anchor, mode]);

  if (!style) return null;

  const n = Number(value);
  const hasValue = value !== "" && Number.isFinite(n) && n > 0;
  const weekdays = mode === "monthly" ? totalWeekdaysIn(months) : 0;
  const preview = mode === "monthly" && hasValue ? monthlyToHourly(n, months) : null;

  return createPortal(
    <div
      ref={rootRef}
      className={`lbr-pay-pop${shown ? " in" : ""}`}
      style={{ top: style.top, left: style.left, width: style.width }}
    >
      <div className="lbr-pay-pop-modes" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "hourly"}
          className={`lbr-pay-pop-mode${mode === "hourly" ? " on" : ""}`}
          onClick={() => onModeChange("hourly")}
        >
          Hourly
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "monthly"}
          className={`lbr-pay-pop-mode${mode === "monthly" ? " on" : ""}`}
          onClick={() => onModeChange("monthly")}
        >
          Monthly
        </button>
      </div>
      <label className="lbr-pay-pop-fld">
        <span>{mode === "monthly" ? "Salary / month" : "Pay / hour"}</span>
        <span className="lbr-pay-pop-box">
          <i>$</i>
          <input
            ref={inputRef}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
          />
        </span>
      </label>
      {mode === "monthly" && (
        <p className="lbr-pay-pop-hint">
          {preview != null ? (
            <>≈ <b>{rate(preview)}</b>/hr this period</>
          ) : (
            "Split across this month's weekdays"
          )}
          {" — "}{weekdays} weekday{weekdays === 1 ? "" : "s"}, {HOURLY_STANDARD_DAY}hr days
        </p>
      )}
    </div>,
    document.body,
  );
};

PayPopover.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  anchor: PropTypes.object,
  mode: PropTypes.oneOf(["hourly", "monthly"]).isRequired,
  value: PropTypes.string.isRequired,
  months: PropTypes.arrayOf(PropTypes.string).isRequired,
  onModeChange: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
PayPopover.defaultProps = { anchor: null };

export default PayPopover;
